import cx from 'classnames';
import { FormV2 } from 'components/ui';
import { ClearIndicator } from 'components/ui/Form/Select';
import { ChevronDown } from 'ethena-icons';
import _ from 'lodash';
import { useEffect, useId } from 'react';
import { Spinner } from 'react-bootstrap';
import RSSelect, {
  ActionMeta,
  components,
  MultiValueProps,
  OptionProps,
  Props,
} from 'react-select';
import css from './Select.module.scss';

export interface ISelectOption<T = any> {
  label: string;
  value: T;
}

interface IMultiValueProps extends MultiValueProps {
  includeAll: boolean;
  label: string;
  totalOptions: number;
}

const MultiValue = ({
  getValue,
  label,
  includeAll,
  index,
  totalOptions,
}: IMultiValueProps) => {
  const selected = getValue() as ISelectOption[];
  if (index > 0) {
    return null;
  } else if (includeAll && selected.length === totalOptions) {
    return <>All {label}</>;
  } else if (selected.length === 1) {
    return <>{selected[index].label}</>;
  } else {
    return (
      <>
        {selected.length} {label}
      </>
    );
  }
};

const Option = (props: OptionProps) => (
  <components.Option {...props}>
    <FormV2.Checkbox
      checked={props.isSelected}
      label={props.label}
      onChange={_.noop}
    />
  </components.Option>
);

interface CheckboxMultiSelectProps extends Props {
  alignMenu?: 'left' | 'right';
  errorMessage?: string;
  includeAll?: boolean;
  isInvalid?: boolean;
  isFluidWidth?: boolean;
  multiValueLabel: string;
  value: ISelectOption[];
}

export const CheckboxMultiSelect = ({
  alignMenu = 'left',
  className,
  errorMessage,
  includeAll = true,
  isFluidWidth,
  isInvalid,
  isSearchable = false,
  menuPlacement = 'auto',
  multiValueLabel,
  onChange,
  options,
  value,
  ...rest
}: CheckboxMultiSelectProps) => {
  const optionsWithoutAll = _.reject(options, { value: 'all' });

  useEffect(() => {
    // If the length of the values equals the length of the options, then we can
    // toggle all of them including "all".
    if (!_.isEmpty(value) && value.length === optionsWithoutAll.length) {
      onChange(options, null);
    }
  }, [JSON.stringify(value)]);

  const handleChange = (selected: any[], event: ActionMeta<unknown>) => {
    const wasSelectAllToggled = _.get(event, 'option.value') === 'all';
    switch (event.action) {
      case 'select-option':
        wasSelectAllToggled || selected.length === optionsWithoutAll.length
          ? onChange(options, null)
          : onChange(selected, null);
        break;
      case 'deselect-option':
        wasSelectAllToggled
          ? onChange([], null)
          : onChange(_.reject(selected, { value: 'all' }), null);
        break;
      default:
        onChange(selected, null);
        break;
    }
  };

  return (
    <div
      className={cx(css.select, className, css[`menu-${alignMenu}`], {
        [css.fluid]: isFluidWidth,
        [css.invalid]: isInvalid,
      })}
    >
      <RSSelect
        classNamePrefix="s"
        closeMenuOnSelect={false}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => <ChevronDown />,
          ClearIndicator: null,
          Option: (props) => <Option {...props} />,
          MultiValue: (props) => (
            <MultiValue
              includeAll={includeAll}
              label={multiValueLabel}
              totalOptions={options.length}
              {...props}
            />
          ),
        }}
        hideSelectedOptions={false}
        isMulti
        isSearchable={isSearchable}
        menuPlacement={menuPlacement}
        onChange={handleChange}
        options={options}
        tabSelectsValue={false}
        value={value}
        {...rest}
      />
      <FormV2.Control.Feedback type="invalid">
        {errorMessage}
      </FormV2.Control.Feedback>
    </div>
  );
};

export interface ISelectProps extends Props {
  alignMenu?: 'left' | 'right';
  errorMessage?: string;
  isFluidWidth?: boolean;
  isInvalid?: boolean;
}

export const Select = ({
  alignMenu = 'left',
  className,
  components,
  errorMessage,
  isClearable,
  isFluidWidth,
  isInvalid,
  isLoading = false,
  isSearchable = false,
  menuPlacement = 'auto',
  ...rest
}: ISelectProps) => {
  return (
    <div
      className={cx(css.select, className, css[`menu-${alignMenu}`], {
        [css.fluid]: isFluidWidth,
        [css.invalid]: isInvalid,
      })}
    >
      <RSSelect
        classNamePrefix="s"
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => <ChevronDown />,
          ClearIndicator: (props) =>
            isClearable ? <ClearIndicator {...props} /> : null,
          LoadingIndicator: () =>
            isLoading ? (
              <div className="mr-2">
                <Spinner
                  animation="border"
                  className={css.spinner}
                  role="status"
                />
              </div>
            ) : null,
          ...components,
        }}
        instanceId={useId()}
        isClearable={isClearable}
        isLoading={isLoading}
        isSearchable={isSearchable}
        menuPlacement={menuPlacement}
        {...rest}
      />
      <FormV2.Control.Feedback type="invalid">
        {errorMessage}
      </FormV2.Control.Feedback>
    </div>
  );
};
